// Sentry must be initialized before any other import so module-load errors
// in App / layer / routes are captured. Keep this as the first import.
// eslint-disable-next-line import/order
import { sentryEnabled } from './sentry-init';

import React from 'react';

import App from '@fastlessjs/backoffice-layer/dist/App';
import { isGodModeActive } from '@fastlessjs/backoffice-layer/dist/hooks';
import { registerHeaderProvider } from '@fastlessjs/backoffice-layer/dist/hooks/stores/helpers';
import debug from '@fastlessjs/backoffice-layer/dist/utils/debug';
import { normalizeOrganizationTheme } from '@fastlessjs/backoffice-layer/dist/utils/theme';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot } from 'react-dom/client';

import { authorizations } from './config/authorizations';
import { ExtraHeaders } from './config/ExtraHeaders';
import { subscribeLegacyGroupPermissions } from './config/legacyGroupPermissions';
import menus from './config/menus';
import routes from './config/routes';
import { createSignUpConfig } from './config/signUp';
// Import translations to register breadcrumb translations with the layer
import './config/translations';

import '@fastlessjs/backoffice-layer/dist/global.less';
import './main.less';

// Build timestamp injected by Vite at build time
declare const APP_BUILD_TIMESTAMP: string;

// Register main app for debug tracking
debug.registerComponent('App');
debug.log('App', 'Application starting...');

// Headers merged into every layer `request()` call (API observability / support)
registerHeaderProvider(() => {
  const headers: Record<string, string> = {};
  if (typeof APP_BUILD_TIMESTAMP !== 'undefined' && APP_BUILD_TIMESTAMP) {
    headers['x-client-build'] = APP_BUILD_TIMESTAMP;
  }
  const viteEnv = import.meta.env?.VITE_ENV;
  if (viteEnv) {
    headers['x-client-env'] = String(viteEnv);
  }
  if (isGodModeActive()) {
    headers['x-god-mode'] = 'true';
  }
  return headers;
});

/**
 * Cache busting: clear app-specific localStorage when a new build is deployed.
 * This prevents stale persisted state from causing issues after releases.
 * Only clears our app stores — Amplify auth tokens (CognitoIdentityServiceProvider) are preserved.
 */
const clearStaleCache = () => {
  const CACHE_VERSION_KEY = 'app_build_version';
  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
  const currentVersion =
    typeof APP_BUILD_TIMESTAMP !== 'undefined' ? APP_BUILD_TIMESTAMP : null;

  if (currentVersion && storedVersion !== currentVersion) {
    debug.log(
      'App',
      `Build version changed (${storedVersion} -> ${currentVersion}), clearing app caches`,
    );

    // Clear all app-specific localStorage keys (preserve Amplify/Cognito tokens)
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key &&
        (key === 'returnStore' ||
          key === 'bo-auth-storage' ||
          key === 'bo-multitenancy-storage' ||
          key.startsWith('secure_'))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      debug.log('App', `Clearing stale cache key: ${key}`);
      localStorage.removeItem(key);
    });

    // Store the new build version
    localStorage.setItem(CACHE_VERSION_KEY, currentVersion);
    debug.log('App', `Cache cleared. ${keysToRemove.length} keys removed.`);

    // Signal that a session refresh is needed after reload
    // The auth Hub listener in useAuth will pick this up and call signIn(true)
    if (keysToRemove.length > 0) {
      localStorage.setItem('app_session_refresh_needed', 'true');
      debug.log('App', 'Reloading to apply clean state...');
      window.location.reload();
    }
  }
};

// Run cache busting before anything else
try {
  clearStaleCache();
} catch (e) {
  debug.error('App', `Cache busting failed: ${e}`);
}

// Global theme storage for use before authentication
declare global {
  interface Window {
    organizationTheme?: {
      template?: { colors?: { primary?: string; secondary?: string } };
      logos?: { dark?: string; light?: string; auth?: string };
      favicon?: string;
      backgroundImage?: string;
    };
  }
}

/**
 * Remove organization theme CSS variables (aligned with useOrganizationTheme in the layer)
 */
const removeThemeColors = (): void => {
  const root = document.documentElement;
  root.style.removeProperty('--color-org-primary');
  root.style.removeProperty('--color-org-primary-light');
  root.style.removeProperty('--color-org-primary-dark');
  root.style.removeProperty('--color-org-primary-bg');
  root.style.removeProperty('--color-org-secondary');
  root.style.removeProperty('--color-org-secondary-light');
  root.style.removeProperty('--color-org-secondary-dark');
  root.style.removeProperty('--color-org-secondary-bg');
  root.style.removeProperty('--ant-primary-color');
  root.style.removeProperty('--ant-secondary-color');
  root.style.removeProperty('--color-primary-org');
  root.style.removeProperty('--color-org-menu-highlight');
  root.style.removeProperty('--color-org-menu-highlight-light');
};

/**
 * Apply theme colors as CSS variables (aligned with useOrganizationTheme in the layer)
 */
const applyThemeColors = (colors?: { primary?: string; secondary?: string }) => {
  const root = document.documentElement;
  removeThemeColors();
  if (!colors) return;

  if (colors.primary) {
    root.style.setProperty('--color-org-primary', colors.primary);
    root.style.setProperty('--ant-primary-color', colors.primary);
    root.style.setProperty('--color-primary-org', colors.primary);
    const hex = colors.primary.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    root.style.setProperty(
      '--color-org-primary-light',
      `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`,
    );
    root.style.setProperty(
      '--color-org-primary-dark',
      `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`,
    );
    root.style.setProperty('--color-org-primary-bg', `rgba(${r}, ${g}, ${b}, 0.1)`);
    debug.log('App', `Primary color applied: ${colors.primary}`);
  }

  if (colors.secondary) {
    root.style.setProperty('--color-org-secondary', colors.secondary);
    root.style.setProperty('--ant-secondary-color', colors.secondary);
    const hex = colors.secondary.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    root.style.setProperty(
      '--color-org-secondary-light',
      `rgb(${Math.min(255, r + 30)}, ${Math.min(255, g + 30)}, ${Math.min(255, b + 30)})`,
    );
    root.style.setProperty(
      '--color-org-secondary-dark',
      `rgb(${Math.max(0, r - 30)}, ${Math.max(0, g - 30)}, ${Math.max(0, b - 30)})`,
    );
    root.style.setProperty('--color-org-secondary-bg', `rgba(${r}, ${g}, ${b}, 0.1)`);
    debug.log('App', `Secondary color applied: ${colors.secondary}`);
  }

  // Menu highlight: when primary is too dark, use secondary or lighten primary (aligned with layer)
  const relativeLuminance = (hexColor: string): number => {
    const h = hexColor.replace('#', '');
    const srgb = [0, 2, 4].map((offset) => {
      const c = parseInt(h.substring(offset, offset + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
  };
  const parseHexRGB = (hexColor: string) => {
    const hex = hexColor.replace('#', '');
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
    };
  };
  const DARK_THRESHOLD = 0.12;
  const primaryLum = colors.primary ? relativeLuminance(colors.primary) : 1;
  let menuHighlight = colors.primary || '';
  if (colors.primary && primaryLum < DARK_THRESHOLD) {
    if (colors.secondary && relativeLuminance(colors.secondary) >= DARK_THRESHOLD) {
      menuHighlight = colors.secondary;
    } else if (colors.primary) {
      const rgb = parseHexRGB(colors.primary);
      menuHighlight = `rgb(${Math.min(255, rgb.r + 80)}, ${Math.min(
        255,
        rgb.g + 80,
      )}, ${Math.min(255, rgb.b + 80)})`;
    }
  }
  if (menuHighlight) {
    root.style.setProperty('--color-org-menu-highlight', menuHighlight);
    if (menuHighlight !== colors.primary && (colors.primary || colors.secondary)) {
      const forParse = menuHighlight.startsWith('#')
        ? menuHighlight
        : colors.secondary || colors.primary || '';
      if (forParse) {
        const mh = parseHexRGB(forParse);
        root.style.setProperty(
          '--color-org-menu-highlight-light',
          `rgb(${Math.min(255, mh.r + 20)}, ${Math.min(255, mh.g + 20)}, ${Math.min(
            255,
            mh.b + 20,
          )})`,
        );
      }
    }
  }
};

/**
 * Fetch and apply organization theme from theme.json
 * This runs before authentication to apply branding on login page
 */
const getThemeJsonUrl = () => `${import.meta.env.BASE_URL}theme.json`;

const loadAndApplyTheme = async () => {
  try {
    const themeJsonUrl = getThemeJsonUrl();
    debug.log('App', `Fetching theme.json from ${themeJsonUrl}...`);
    const response = await fetch(themeJsonUrl);
    if (!response.ok) {
      debug.log('App', 'No theme.json found, using defaults');
      return null;
    }
    const rawTheme = await response.json();
    const theme = normalizeOrganizationTheme(rawTheme);
    debug.log('App', `Theme loaded: ${JSON.stringify(theme)}`);

    // Store theme globally for signUp config
    window.organizationTheme = theme;

    // Apply theme colors immediately (for login page styling)
    if (theme?.template?.colors) {
      applyThemeColors(theme.template.colors);
    }

    // Apply favicon immediately
    if (theme?.favicon) {
      const existingFavicons = document.querySelectorAll('link[rel="icon"]');
      existingFavicons.forEach((el) => el.parentNode?.removeChild(el));

      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = theme.favicon;
      if (theme.favicon.endsWith('.svg')) link.type = 'image/svg+xml';
      else if (theme.favicon.endsWith('.png')) link.type = 'image/png';
      document.head.appendChild(link);
      debug.log('App', `Favicon applied: ${theme.favicon}`);
    }

    // Apply auth background image
    if (theme?.backgroundImage) {
      document.documentElement.style.setProperty(
        '--auth-background-image',
        `url('${theme.backgroundImage}')`,
      );
      debug.log('App', `Auth background applied: ${theme.backgroundImage}`);
    }

    return theme;
  } catch (e) {
    debug.log('App', `Failed to load theme.json: ${e}`);
    return null;
  }
};

(async () => {
  debug.log('App', 'Loading configuration...');

  // Load theme first (before authentication) for login page branding
  const theme = await loadAndApplyTheme();
  const loadedConfig: Record<string, any> = import.meta.env.PROD
    ? (await import('../config.json')).default
    : {};

  const envBool = (v: unknown): boolean =>
    v === true || String(v ?? '').toLowerCase() === 'true';

  // Dev: config.json is not loaded unless PROD — merge critical Vite env vars so Rules / superadmin
  // gates match loadConfig() (see backoffice-layer Provider + useDynamicConfig).
  const config = {
    ...loadedConfig,
    VITE_DEFAULT_API_URL:
      loadedConfig.VITE_DEFAULT_API_URL ?? import.meta.env.VITE_DEFAULT_API_URL,
    VITE_ORGANIZATION:
      loadedConfig.VITE_ORGANIZATION ?? import.meta.env.VITE_ORGANIZATION,
    VITE_ENABLE_BULK_ACTIONS: false,
    VITE_ENABLE_RULES_MANAGEMENT: envBool(
      loadedConfig.VITE_ENABLE_RULES_MANAGEMENT ??
        import.meta.env.VITE_ENABLE_RULES_MANAGEMENT,
    ),
  };
  debug.log('App', `Environment: ${import.meta.env.VITE_ENV || 'development'}`);

  if (sentryEnabled) {
    const organization = (config as { VITE_ORGANIZATION?: string })?.VITE_ORGANIZATION;
    if (organization) Sentry.setTag('organization', organization);
    const runtimeEnv = (config as { VITE_ENV?: string })?.VITE_ENV;
    if (runtimeEnv) Sentry.setTag('runtime.env', runtimeEnv.toLowerCase());
  }

  // Create signUp config with theme (for auth logo)
  const signUp = createSignUpConfig(theme);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        notifyOnChangeProps: ['data', 'error', 'isLoading'],
      },
    },
  });

  const Application = (
    <QueryClientProvider client={queryClient}>
      <App
        routes={routes}
        menus={menus}
        config={config}
        layoutOptions={{ extraHeader: ExtraHeaders }}
        signUp={signUp}
        authorizations={authorizations}
      />
    </QueryClientProvider>
  );

  const RootComponent = sentryEnabled ? (
    <Sentry.ErrorBoundary fallback={<div>An error occurred</div>}>
      {Application}
    </Sentry.ErrorBoundary>
  ) : (
    Application
  );

  subscribeLegacyGroupPermissions();

  const root = createRoot(document.getElementById('root')!);
  root.render(RootComponent);
  debug.log('App', 'Application rendered successfully');
  debug.markInitialized('App');
})();
